import { BehaviorSubject } from "rxjs";

import { ds } from "js/core/models/dataService";
import firebase from "js/firebase";
import pusher from "js/core/services/pusher";
import getLogger, { LogGroup } from "js/core/logger";

const logger = getLogger(LogGroup.COLLABORATION);

class CollaborationSlideLockService {
    constructor(slide) {
        this.slide = slide;

        // Starting from empty
        this._lockState = {};

        // Slide lock observable
        this._lock$ = new BehaviorSubject(this.lockState);
        this.lock$ = this._lock$.asObservable();

        // Timeout used to let the listeners know when current lock expires
        this._refreshLockTimeout = null;

        this._initializePromise = null;

        // Initializing
        this._initialize()
            .catch(err => {
                logger.error(err, `[CollaborationSlideLockService] initial initialization failed`, { slideId: this.slide?.id });
            });
    }

    get presentationId() {
        return this.slide.presentation?.id || ds.selection.presentation?.id;
    }

    get slideId() {
        return this.slide.id;
    }

    get uid() {
        return firebase.auth().currentUser.uid;
    }

    /**
     * Can lock current slide (slide donsn't have any valid locks by other collaborators)
     */
    get canLock() {
        return this._lockState.lockedBy?.uid === this.uid || (this._lockState.lockedUntil || 0) < Date.now();
    }

    /**
     * Current slide is currently locked by a collaborator
     */
    get isLocked() {
        return this._lockState.lockedBy?.uid !== this.uid && (this._lockState.lockedUntil || 0) >= Date.now();
    }

    /**
     * The last lock on current slide was posted by me
     */
    get isLockedByMe() {
        return this._lockState.lockedBy?.uid === this.uid && (this._lockState.lockedUntil || 0) >= Date.now();
    }

    /**
     * Enrich representation of current lock state
     */
    get lockState() {
        return {
            ...this._lockState,
            isLocked: this.isLocked,
            isLockedByMe: this.isLockedByMe,
            canLock: this.canLock
        };
    }

    _getUser(uid) {
        return this.pusherChannel.members.get(uid).info;
    }

    _setLock(uid, lockedUntil) {
        clearTimeout(this._refreshLockTimeout);

        this._lockState = {
            lockedBy: this._getUser(uid),
            lockedUntil
        };
        this._lock$.next(this.lockState);

        // If there's a lock, the set refresh lock timeout to let
        // the listeners know when the lock expires
        this._refreshLockTimeout = setTimeout(() => {
            this._lock$.next(this.lockState);
        }, this.lockState.lockedUntil - Date.now() + 1);
    }

    _removeLock() {
        clearTimeout(this._refreshLockTimeout);

        this._lockState = {};
        this._lock$.next(this.lockState);
    }

    _initialize() {
        this._initializePromise = (async () => {
            this.pusherChannel = await pusher.subscribe(`presence-slide-lock-${this.slideId}@${this.presentationId ?? ""}`);
            if (!this.pusherChannel) {
                // Didn't subscribe (pusher is disabled)
                return;
            }

            this.pusherChannel.bind("pusher:member_added", () => {
                if (this.isLockedByMe) {
                    // Let new user know slide is locked
                    this.pusherChannel.trigger("client-lock", { lockedUntil: this.lockState.lockedUntil });
                }
            });

            this.pusherChannel.bind("pusher:member_removed", ({ id: uid }) => {
                if (this.isLocked && this.lockState.lockedBy.uid === uid) {
                    this._removeLock();
                }
            });

            this.pusherChannel.bind("client-lock", ({ lockedUntil }, { user_id: uid }) => {
                // The slide may be locked by me, this can happen because there's a time gap between when we
                // render the slide and when the subscription to the channel is established and during this gap
                // we don't want to block the user from editing because it will deteriorate the UIX, so we just
                // allow this edge case to happen given that it's quite rare
                if (!this.isLockedByMe) {
                    // The slide was locked by uid
                    this._setLock(uid, lockedUntil);
                }
            });

            this.pusherChannel.bind("client-unlock", () => {
                // The slide was unlocked
                this._removeLock();
            });
        })();

        return this._initializePromise;
    }

    /**
     * Lock the slide for the given time
     */
    lock(lockTimeSeconds = 5) {
        if (!this.canLock) {
            throw new Error("Cannot lock");
        }

        (async () => {
            try {
                // Wait for the initialize promise to allow calling before the initialization
                // has finished
                await this._initializePromise
                    .catch(err => {
                        // If failed to initialize then try once again (e.g. went back online)
                        logger.error(err, `[CollaborationSlideLockService] initialization failed, trying again...`, { slideId: this.slideId });
                        return this._initialize();
                    });

                this._setLock(this.uid, Date.now() + lockTimeSeconds * 1000);
                this.pusherChannel.trigger("client-lock", { lockedUntil: this.lockState.lockedUntil });
            } catch (err) {
                logger.error(err, `[CollaborationSlideLockService] cannot lock, initialization failed`, { slideId: this.slideId });
            }
        })();
    }

    /**
     * Explicitly unlock the slide (before the lock expires)
     */
    unlock() {
        if (this.isLocked) {
            throw new Error("Cannot unlock");
        }

        if (this.isLockedByMe) {
            this._removeLock();
            this.pusherChannel.trigger("client-unlock", {});
        }
    }

    dispose() {
        clearTimeout(this._refreshLockTimeout);

        // Make sure we've initialized so we don't unsubscribe before initialization
        this._initializePromise
            .then(() => {
                if (this.pusherChannel) {
                    this.pusherChannel.unbind_all();
                    this.pusherChannel.unsubscribe();
                    this.pusherChannel = null;
                }
            });
    }
}

export default CollaborationSlideLockService;
